import type { UploadedFile } from "@taxassist/api-client";

/**
 * Generic file item interface that can represent both uploaded files and processing results
 */
export interface FileItem {
  /** Unique identifier for the file */
  id: string;

  /** Display name of the file */
  fileName: string;

  /** File size in bytes */
  fileSize: number;

  /** MIME content type */
  contentType: string;

  /** When the file was uploaded/created */
  uploadedAt: string;

  /** S3 key for file access */
  s3Key: string;

  /** Optional description or notes */
  description?: string;

  /** File type/category for display purposes */
  type?: string;

  /** Display label for the file type */
  typeLabel?: string;

  /** Who uploaded the file */
  uploadedBy?: string;

  /** Email of uploader */
  uploadedByEmail?: string;

  /** Additional metadata specific to file type */
  metadata?: Record<string, any>;

  expectedBucket?: string;

  /** Whether this file is new (uploaded after the last submission) */
  isNew?: boolean;
}

export type BucketType = "sureprep" | "k1_complex" | "unsupported";

/**
 * Action handlers for file operations
 */
export interface FileListCallbacks {
  /** Called when user clicks on a file to preview */
  onPreview?: (file: FileItem, index: number) => void;

  /** Called when user clicks download button */
  onDownload?: (file: FileItem) => void;

  /** Called when user clicks delete button */
  onDelete?: (file: FileItem) => Promise<void>;

  /** Function to get presigned URLs for file preview */
  getFileUrl?: (file: FileItem) => Promise<string>;

  /** Called when user updates the file's type via the type menu */
  onUpdateFileType?: (
    file: FileItem,
    newType: ProcessingArtifactType | UploadedFileType,
  ) => Promise<void> | void;

  /** Called when user updates the file's bucket via the bucket menu */
  onUpdateBucket?: (
    file: FileItem,
    newBucket: BucketType,
  ) => Promise<void> | void;
}

/**
 * Configuration options for file list display
 */
export interface FileListConfig {
  /** Whether to show action buttons */
  showActions?: boolean;

  /** Compact display mode */
  compact?: boolean;

  /** Maximum number of files to display */
  maxItems?: number;

  /** Whether to allow file deletion */
  allowDelete?: boolean;

  /** Whether to enable file viewer dialog */
  enableViewer?: boolean;

  /** Custom empty state message */
  emptyMessage?: string;

  /** Custom empty state icon */
  emptyIcon?: React.ReactElement;

  /** Whether to show file type badges */
  showTypeBadges?: boolean;

  /** Whether type badges are read-only (no dropdown menu) */
  readOnlyTypeBadges?: boolean;

  /** Custom styling overrides */
  sx?: Record<string, any>;
}

/**
 * File submission status evaluator function type
 */
export type FileSubmissionEvaluator = (file: FileItem) => boolean;

/**
 * Adapter function to convert UploadedFile to FileItem
 */
export const convertUploadedFileToGeneric = (file: UploadedFile): FileItem => ({
  id: file.s3Key,
  fileName: file.fileName,
  fileSize: file.fileSize || 0,
  contentType: file.contentType || "application/octet-stream",
  uploadedAt: file.uploadedAt,
  s3Key: file.s3Key,
  expectedBucket: (file as any).expectedBucket,
});

/**
 * Adapter function to convert FileItem back to UploadedFile
 */
export const convertGenericToUploadedFile = (file: FileItem): UploadedFile => ({
  fileName: file.fileName,
  s3Key: file.s3Key,
  fileSize: file.fileSize,
  contentType: file.contentType,
  uploadedAt: file.uploadedAt,
});

export type ProcessingArtifactType =
  | "binder"
  | "k1_worksheets"
  | "supporting_doc";
export type ProcessingArtifactLabel =
  | "BINDER"
  | "K1X IMPORT"
  | "TAX SOFTWARE IMPORT";

export interface ProcessingArtifactItem extends FileItem {
  type: ProcessingArtifactType;
  typeLabel: ProcessingArtifactLabel;
}

export type UploadedFileType = "w2" | "1099" | "k1" | "other";
export type UploadedFileTypeLabel = "W-2" | "1099" | "K-1" | "OTHER";

export interface UploadedFileItem extends FileItem {
  type: UploadedFileType;
  typeLabel: UploadedFileTypeLabel;
}

export const convertProcessingArtifactToGeneric = (artifact: {
  artifactId: string;
  fileName: string;
  fileSize: number;
  contentType: string;
  uploadedAt: string;
  s3Key: string;
  description?: string;
  artifactType: ProcessingArtifactType;
  uploadedBy?: string;
  uploadedByEmail?: string;
}): ProcessingArtifactItem => ({
  id: artifact.artifactId,
  fileName: artifact.fileName,
  fileSize: artifact.fileSize,
  contentType: artifact.contentType,
  uploadedAt: artifact.uploadedAt,
  s3Key: artifact.s3Key,
  description: artifact.description,
  type: artifact.artifactType,
  typeLabel: getArtifactTypeLabel(artifact.artifactType),
  uploadedBy: artifact.uploadedBy,
  uploadedByEmail: artifact.uploadedByEmail,
});

/**
 * Helper function to get display label for artifact types
 */
export const ARTIFACT_TYPE_LABELS: Record<
  ProcessingArtifactType,
  ProcessingArtifactLabel
> = {
  binder: "BINDER",
  k1_worksheets: "K1X IMPORT",
  supporting_doc: "TAX SOFTWARE IMPORT",
};

export const getArtifactTypeLabel = (
  type: ProcessingArtifactType,
): ProcessingArtifactLabel => ARTIFACT_TYPE_LABELS[type] ?? "OTHER";

/**
 * Helper function to get display label for uploaded file types
 */
export const UPLOADED_FILE_TYPE_LABELS: Record<
  UploadedFileType,
  UploadedFileTypeLabel
> = {
  w2: "W-2",
  "1099": "1099",
  k1: "K-1",
  other: "OTHER",
};

export const getUploadedFileTypeLabel = (
  type: UploadedFileType,
): UploadedFileTypeLabel => UPLOADED_FILE_TYPE_LABELS[type] ?? "OTHER";

/**
 * Bucket configuration for display
 */
export const BUCKET_CONFIG: Record<
  BucketType,
  { label: string; bgcolor: string; color: string }
> = {
  sureprep: { label: "SP", bgcolor: "#EEEEEE", color: "#757575" },
  k1_complex: { label: "K1X", bgcolor: "#424242", color: "#FFFFFF" },
  unsupported: { label: "N/A", bgcolor: "#CD5437", color: "#FFFFFF" },
};

export const BUCKET_LABELS: Record<BucketType, string> = {
  sureprep: "SP",
  k1_complex: "K1X",
  unsupported: "N/A",
};

export const BUCKET_SUBHEADER_LABELS: Record<string, string> = {
  sureprep: "Ready to submit",
  k1_complex: "Complex K1s",
  unsupported: "Unsupported",
};
